import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, Download, BookMarked, Lock } from "lucide-react";

const GRADE_LABEL = (g) => (g === "K" ? "K" : g);

export default function Workbooks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/workbooks").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const grades = useMemo(() => [...new Set(items.map((i) => i.grade_norm))].sort((a, b) => (a === "K" ? -1 : Number(a) - Number(b))), [items]);
  const subjects = useMemo(() => [...new Set(items.map((i) => i.subject))].sort(), [items]);
  const filtered = items.filter((i) => (!grade || i.grade_norm === grade) && (!subject || i.subject === subject));

  const download = async (wb) => {
    if (!user) { navigate("/login"); return; }
    setDownloading(wb.workbook_id);
    try {
      const { data } = await api.get(`/workbooks/${wb.workbook_id}/download`);
      if (data.file_url) window.open(data.file_url, "_blank", "noopener");
      else toast.error("File not available.");
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) { toast.error("Enroll or start a membership to download workbooks."); navigate("/pricing"); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
      <div className="border-b border-border pb-8">
        <span className="label-caps text-primary">Printable Resources</span>
        <h1 className="mt-2 font-head text-4xl font-black sm:text-5xl">Workbooks</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">Downloadable PDF workbooks to reinforce lessons off-screen. Enrollment or membership unlocks the downloads.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={() => setGrade("")} className={`border px-4 py-2 label-caps transition-colors ${!grade ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`} data-testid="wb-grade-all">All Grades</button>
            {grades.map((g) => (
              <button key={g} onClick={() => setGrade(g)} data-testid={`wb-grade-${g}`} className={`min-w-[46px] border px-3 py-2 label-caps transition-colors ${grade === g ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{GRADE_LABEL(g)}</button>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => setSubject("")} className={`border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${!subject ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`} data-testid="wb-subject-all">All Subjects</button>
            {subjects.map((s) => (
              <button key={s} onClick={() => setSubject(s)} data-testid={`wb-subject-${s}`} className={`border px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${subject === s ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{s}</button>
            ))}
          </div>

          <p className="mt-8 label-caps text-muted-foreground" data-testid="wb-count">{filtered.length} Workbooks</p>
          {filtered.length === 0 ? (
            <div className="mt-4 border border-border bg-card p-16 text-center text-muted-foreground">No workbooks match your filters.</div>
          ) : (
            <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((wb) => (
                <div key={wb.workbook_id} className="flex flex-col justify-between border border-border bg-card p-6 transition-all hover:border-primary" data-testid={`wb-card-${wb.workbook_id}`}>
                  <div>
                    <div className="flex items-start justify-between">
                      <BookMarked className="h-8 w-8 text-primary" />
                      <span className="label-caps text-[0.6rem] text-muted-foreground">Grade {GRADE_LABEL(wb.grade_norm)}</span>
                    </div>
                    <h3 className="mt-4 font-head text-xl font-bold leading-tight">{wb.title}</h3>
                    <p className="mt-1 label-caps text-[0.6rem] text-muted-foreground">{wb.subject}</p>
                  </div>
                  <Button onClick={() => download(wb)} disabled={downloading === wb.workbook_id} className="mt-5 w-full rounded-none label-caps bg-primary hover:bg-primary/80" data-testid={`wb-download-${wb.workbook_id}`}>
                    {downloading === wb.workbook_id ? <Loader2 className="h-4 w-4 animate-spin" /> : user ? <><Download className="mr-2 h-4 w-4" /> Download PDF</> : <><Lock className="mr-2 h-4 w-4" /> Log In to Download</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
